import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../services/auth.service';
import { DirectionService } from '../../services/direction.service';
import { Subject, takeUntil } from 'rxjs';
import { PaginatorComponent } from '../../shared/paginator/paginator.component';
import { IPaginator } from '../../components/strategic-project/strategic-project.component';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { NavTitlePipe } from '../../pipes/nav-title.pipe';

@Component({
  selector: 'app-project-edit-permissions',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    TableModule,
    InputTextModule,
    PaginatorComponent,
    ToggleButtonModule,
    NavTitlePipe,
  ],
  templateUrl: './project-edit-permissions.component.html',
  styleUrl: './project-edit-permissions.component.scss'
})
export class ProjectEditPermissionsComponent implements OnInit, OnDestroy {
  unsubscribe$ = new Subject<void>();
  dir!: 'rtl' | 'ltr';
  users: any[] = [];
  filteredUsers: any[] = [];
  searchTerm = '';
  paginator: IPaginator = {} as IPaginator;
  pageNumber = 1;
  page_size = 10;
  isLoading = true;

  constructor(
    private authService: AuthService,
    private toastr: ToastrService,
    private directionService: DirectionService
  ) {}

  ngOnInit(): void {
    this.directionService.direction$.pipe(takeUntil(this.unsubscribe$)).subscribe((dir) => {
      this.dir = dir;
    });
    this.fetchUsers();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private fetchUsers() {
    this.isLoading = true;
    this.authService.ListPaginatedUsers({
      page: this.pageNumber,
      page_size: this.page_size,
      filters: 'all'
    }).subscribe({
      next: (response: any) => {
        this.users = response['data']['users'].map((user: any) => ({
          ...user,
          // Preserve the actual value from database, default to true only if null/undefined
          can_edit_projects: user.can_edit_projects ?? true
        }));
        this.filteredUsers = this.users;
        this.paginator = response['data']['pagination'] as IPaginator;
        this.onSearch();
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.toastr.error(
          this.dir === 'rtl' ? 'فشل تحميل المستخدمين' : 'Failed to load users',
          this.dir === 'rtl' ? 'خطأ' : 'Error'
        );
      }
    });
  }

  clearSearch() {
    this.searchTerm = '';
    this.onSearch();
  }

  getAllowedCount(): number {
    return (this.users || []).filter((u) => !!u.can_edit_projects).length;
  }

  getBlockedCount(): number {
    return (this.users || []).filter((u) => !u.can_edit_projects).length;
  }

  getInitials(value: string): string {
    if (!value) return '?';
    const parts = value.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }

  onPageChange(page: number) {
    this.pageNumber = page;
    this.fetchUsers();
  }

  onPageSizeChange(pageSize: number) {
    this.page_size = pageSize;
    this.pageNumber = 1; // Reset to first page when page size changes
    this.fetchUsers();
  }

  onSearch() {
    if (!this.searchTerm.trim()) {
      this.filteredUsers = this.users;
      return;
    }
    const term = this.searchTerm.toLowerCase();
    this.filteredUsers = this.users.filter(user =>
      user.name?.toLowerCase().includes(term) ||
      user.username?.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term)
    );
  }

  toggleEditPermission(user: any, newValue: boolean) {
    // Store the original value before change
    const originalValue = user.can_edit_projects ?? true;
    
    // Optimistically update the UI
    user.can_edit_projects = newValue;
    
    this.authService.updateUserEditPermission(user.id, { can_edit_projects: newValue }).subscribe({
      next: (response: any) => {
        // Handle both full HttpResponse and direct response body
        const responseBody = response?.body || response;
        
        // Check if the response indicates success
        // BaseHttpResponse has Success property and data.success
        if (responseBody && (responseBody.Success === true || responseBody.success === true || responseBody.data?.success === true)) {
          // Value already updated optimistically, just show success message
          this.toastr.success(
            this.dir == 'rtl' 
              ? `تم ${newValue ? 'السماح' : 'منع'} المستخدم من تعديل المشاريع` 
              : `User ${newValue ? 'allowed' : 'blocked'} from editing projects`,
            this.dir == 'rtl' ? 'نجاح' : 'Success'
          );
        } else {
          // Revert the toggle if update failed
          user.can_edit_projects = originalValue;
          this.toastr.error(
            this.dir == 'rtl' ? 'حدث خطأ أثناء التحديث' : 'Error updating permission',
            this.dir == 'rtl' ? 'خطأ' : 'Error'
          );
        }
      },
      error: (err) => {
        // Revert the toggle on error
        user.can_edit_projects = originalValue;
        console.error('Error updating user edit permission:', err);
        console.error('Full error object:', JSON.stringify(err, null, 2));
        
        let errorMessage = 'Unknown error';
        let errorDetails = '';
        
        // Handle status 0 (network/CORS error)
        if (err?.status === 0) {
          errorMessage = this.dir == 'rtl' 
            ? 'فشل الاتصال بالخادم' 
            : 'Failed to connect to server';
          errorDetails = this.dir == 'rtl'
            ? 'تحقق من: 1) الاتصال بالإنترنت 2) إعدادات CORS 3) أن الخادم يعمل 4) شهادة SSL'
            : 'Check: 1) Internet connection 2) CORS settings 3) Server is running 4) SSL certificate';
        } else if (err?.status === 404) {
          errorMessage = this.dir == 'rtl' 
            ? 'الـ endpoint غير موجود' 
            : 'Endpoint not found';
          errorDetails = this.dir == 'rtl'
            ? 'تأكد من تحديث الـ backend'
            : 'Please ensure the backend is updated';
        } else if (err?.status === 401 || err?.status === 403) {
          errorMessage = this.dir == 'rtl' 
            ? 'ليس لديك صلاحية للقيام بهذا الإجراء' 
            : 'You do not have permission to perform this action';
        } else if (err?.error?.error) {
          // Handle detailed error from backend
          const backendError = err.error.error;
          errorMessage = backendError.message || backendError.messageText || 'Server error';
          if (backendError.databaseError) {
            errorDetails = `Database: ${backendError.databaseError.messageText || ''}`;
          }
          if (backendError.solution) {
            errorDetails += ` | Solution: ${backendError.solution}`;
          }
        } else if (err?.error?.message) {
          errorMessage = err.error.message;
        } else if (err?.message) {
          errorMessage = err.message;
        }
        
        const fullErrorMessage = errorDetails ? `${errorMessage} - ${errorDetails}` : errorMessage;
        
        this.toastr.error(
          this.dir == 'rtl' 
            ? `حدث خطأ أثناء التحديث: ${fullErrorMessage}` 
            : `Error updating permission: ${fullErrorMessage}`,
          this.dir == 'rtl' ? 'خطأ' : 'Error',
          {
            timeOut: 10000, // Show for 10 seconds
            enableHtml: true
          }
        );
      }
    });
  }
}

